import {
  SwHeightClamps,
  SwHeightEquationType,
  SwHeightMethod
} from '../../@types/swHeight/types';

/**
 * Shared shape of the Sw-height calibration controls.
 *
 * These live in a constants module rather than on a component because the
 * controls are split across four of them — the rail, the three drawer
 * bodies — plus the calculated table, which needs `PARAMETER_LABELS` to
 * name the coefficients it echoes.
 */

/**
 * Sign convention, confirmed against the reference workbook
 * ('Core Domain Pc Model'!F2 - !F1 = 0.45 - 0.37 = 0.08 psi/ft) and
 * against `liao.py`, which computes height as Pc / (water_grad - oil_grad).
 * Shown as the field's hint so the sign is never ambiguous.
 */
export const GRADIENT_CONVENTION = 'Brine gradient − Oil gradient';

export const EQUATION_OPTIONS = [
  { value: 'linear', label: 'Linear' },
  { value: 'power', label: 'Power' },
  { value: 'logarithmic', label: 'Logarithmic' },
  { value: 'exponential', label: 'Exponential' }
];

/**
 * What the two coefficients mean depends on the fitted form, so the labels
 * follow the selected equation. Taken from
 * `strategies/sw_height.py::evaluate_tendency`, which is what actually
 * evaluates them:
 *
 *   linear       p1*x + p2        -> p1 is the slope, p2 the intercept
 *   logarithmic  p1 + p2*ln(x)    -> p1 is the intercept, p2 the slope on ln x
 *   power        p1 * x^p2        -> coefficient and exponent
 *   exponential  p1 * e^(p2*x)    -> scale and rate
 *
 * Note the first two are opposites: calling p1 "Intercept" is right for the
 * logarithmic fit and wrong for the linear one. The reference workbook sides
 * with this — its linear Pe is `$Q$6*RQI + $R$6`, so Parameter 1 is the slope.
 */
export const PARAMETER_LABELS: Record<
  SwHeightEquationType,
  { p1: string; p2: string }
> = {
  linear: { p1: 'Slope', p2: 'Intercept' },
  logarithmic: { p1: 'Intercept', p2: 'Slope (per ln x)' },
  power: { p1: 'Coefficient', p2: 'Exponent' },
  exponential: { p1: 'Scale', p2: 'Rate' }
};

export const DEFAULT_EQUATION: SwHeightEquationType = 'linear';

/**
 * The bounds applied after each tendency is evaluated. Shown in two tiers:
 * the Swirr pair and the permeability cut-off routinely bind on real data,
 * whereas the Pe and lambda floors are guards that rarely fire -- so they
 * sit behind a disclosure rather than competing for attention.
 */
export const CLAMP_FIELDS: {
  key: keyof SwHeightClamps;
  label: string;
  hint: string;
  advanced: boolean;
  min?: number;
  step: number;
}[] = [
  {
    key: 'swirr_min',
    label: 'Swirr minimum',
    hint: 'Lowest irreducible water saturation',
    advanced: false,
    min: 0,
    step: 0.01
  },
  {
    key: 'swirr_max',
    label: 'Swirr maximum',
    hint: 'Above this the rock is not pay',
    advanced: false,
    min: 0,
    step: 0.01
  },
  {
    key: 'perm_cutoff',
    label: 'Reservoir cut-off (mD)',
    hint: 'Below this, Sw is set to 1',
    advanced: false,
    min: 0,
    step: 0.01
  },
  {
    key: 'pe_min',
    label: 'Pe floor (psi)',
    hint: 'Must stay above zero: Pe is a denominator',
    advanced: true,
    step: 0.1
  },
  {
    key: 'lambda_min',
    label: 'Lambda floor',
    hint: 'Must stay above zero, or the model inverts',
    advanced: true,
    step: 0.01
  }
];

export type ParameterKey =
  | 'swirr_params'
  | 'pe_params'
  | 'lambda_params'
  | 'best_a_params'
  | 'best_b_params'
  | 'true_pc_max_params';

export type ParameterRow = {
  key: ParameterKey;
  label: string;
  defaultsKey: 'swirr' | 'pe' | 'lambda' | 'best_a' | 'best_b' | 'true_pc_max';
};

/**
 * Which tendency fits each methodology samples per row, in display order.
 * These drive the `Sw (model)` curve.
 *
 * Swirr and Pe are shared: `TENDENCY_FIELD_MAP` maps the correlation's
 * `swir` and `pce` fits onto them, and Liao needs exactly those two as its
 * irreducible saturation and entry pressure. Liao replaces the Lambda row
 * with a / b / Pc max, since its lambda is derived from those three rather
 * than fitted (see `strategies/sw_height.py::liao_lambda`).
 */
export const PARAMETER_ROWS: Record<SwHeightMethod, ParameterRow[]> = {
  brooks_corey: [
    { key: 'swirr_params', label: 'Swirr', defaultsKey: 'swirr' },
    { key: 'pe_params', label: 'Pe', defaultsKey: 'pe' },
    { key: 'lambda_params', label: 'Lambda', defaultsKey: 'lambda' }
  ],
  liao: [
    { key: 'swirr_params', label: 'Swirr', defaultsKey: 'swirr' },
    { key: 'pe_params', label: 'Pe', defaultsKey: 'pe' },
    { key: 'best_a_params', label: 'a', defaultsKey: 'best_a' },
    { key: 'best_b_params', label: 'b', defaultsKey: 'best_b' },
    { key: 'true_pc_max_params', label: 'Pc max', defaultsKey: 'true_pc_max' }
  ]
};

export const METHOD_OPTIONS = [
  { value: 'brooks_corey', label: 'Brooks-Corey' },
  { value: 'liao', label: 'Liao' }
];

export const METHOD_HINT: Record<SwHeightMethod, string> = {
  brooks_corey: 'Sw = Swirr + (1 − Swirr)·(Pc/Pe)^(−1/λ)',
  liao: 'Lambda is derived from a, b and Pe'
};

/**
 * The three parameter groups that sit behind buttons rather than in the
 * rail: each is a table or a column of fields, too tall to keep on screen
 * beside the charts.
 */
export type SwHeightSection = 'cutoffs' | 'parameters' | 'rock-types';

/** Heading for each section, reused verbatim as its drawer title. */
export const SECTION_TITLES: Record<SwHeightSection, string> = {
  cutoffs: 'Cut-offs',
  parameters: 'Sw (model) — from the tendency fits',
  'rock-types': 'Sw (model, per rock type) — one parameter set per FZI bin'
};

/** Short label for the button that opens each section. */
export const SECTION_BUTTON_LABELS: Record<SwHeightSection, string> = {
  cutoffs: 'Cut-offs',
  parameters: 'Sw (model)',
  'rock-types': 'Sw (model, per rock type)'
};

export const numberOrNull = (raw: string): number | null => {
  if (raw.trim() === '') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};
